// Two-item Bundle Builder JavaScript (sections/custom-bundle-builder-two.liquid)
// Separate from the starter pack's custom-bundle-builder.js. Picks are kept in
// localStorage under a per-page key (data-storage-key) because the same
// section/template is reused on several pages.
// Each category is a curated product list (rendered server-side in Liquid).
// The customer picks one product via an inline thumbnail grid, then one size
// from a shared swatch row scoped to that category.

(function() {
  'use strict';

  // ---------- Global State ----------
  // bundleItems: one entry per category position that has a product+size selected.
  let bundleItems = [];
  let categories = []; // parsed from #bundle-builder-data: [{ position, heading, products }]
  let submitAttempted = false; // only surface the incomplete-category message after a submit attempt

  const sectionEl = document.querySelector('[data-two-item-bundle]');
  if (!sectionEl) return;
  const storageKey = sectionEl.dataset.storageKey;

  const gridColumns = parseInt(sectionEl.dataset.gridColumns) || 6;
  const redirectToCart = sectionEl.dataset.redirectToCart === 'true';
  const parentVariantId = sectionEl.dataset.parentVariantId;

  const isMobile = window.innerWidth <= 768;
  const effectiveColumns = isMobile ? Math.min(gridColumns, 4) : gridColumns;
  document.documentElement.style.setProperty('--grid-columns', effectiveColumns);

  // ---------- Helper Functions ----------
  function fixImageUrl(url) {
    if (!url) return '';
    if (typeof url !== 'string') return '';
    if (url.startsWith('//')) return 'https:' + url;
    return url;
  }

  function getCategory(position) {
    return categories.find(c => c.position === position);
  }

  function getProduct(position, productIndex) {
    const category = getCategory(position);
    return category ? category.products[productIndex] : null;
  }

  // ---------- Bundle Storage (localStorage, per page) ----------
  function loadBundle() {
    try {
      const raw = localStorage.getItem(storageKey);
      bundleItems = raw ? JSON.parse(raw) : [];
    } catch(e) { bundleItems = []; }
    if (!Array.isArray(bundleItems)) bundleItems = [];
  }

  function clearBundle() {
    try { localStorage.removeItem(storageKey); } catch(e) {}
  }

  function saveBundle() {
    // localStorage can throw (private mode / quota) - picks just won't persist.
    try { localStorage.setItem(storageKey, JSON.stringify(bundleItems)); } catch(e) {}
    updateBundleTotal();
    updateCartMessage();
  }

  function updateBundleTotal() {
    const totalSpan = document.getElementById('bundle-total-items');
    if (totalSpan) totalSpan.textContent = bundleItems.length;
  }

  function getItemAt(position) {
    return bundleItems.find(item => item.position === position) || null;
  }

  function setItemAt(position, item) {
    bundleItems = bundleItems.filter(i => i.position !== position);
    if (item) bundleItems.push(item);
    saveBundle();
  }

  // ---------- Group Tabs (e.g. Inseam: 5" / 7" / 9") ----------
  // Sub-grouping is a pure display filter: productIndex/position stay the
  // same as the ungrouped path, only which thumbnails are visible changes.
  function setActiveGroup(position, groupIndex) {
    const tabs = document.getElementById(`group-tabs-${position}`);
    if (tabs) {
      tabs.querySelectorAll('.bundle-group-tab').forEach(tab => {
        const isActive = parseInt(tab.dataset.group, 10) === groupIndex;
        tab.classList.toggle('active', isActive);
        tab.setAttribute('aria-checked', isActive ? 'true' : 'false');
      });
    }

    const grid = document.getElementById(`thumbnail-grid-${position}`);
    if (grid) {
      grid.querySelectorAll('.bundle-thumbnail[data-group]').forEach(btn => {
        const matches = parseInt(btn.dataset.group, 10) === groupIndex;
        btn.classList.toggle('group-hidden', !matches);
      });
    }
  }

  function initGroupTabs() {
    document.querySelectorAll('.bundle-group-tab').forEach(tab => {
      tab.addEventListener('click', () => {
        const position = parseInt(tab.dataset.position, 10);
        const groupIndex = parseInt(tab.dataset.group, 10);
        setActiveGroup(position, groupIndex);
      });
    });
  }

  // ---------- Thumbnail Grid ----------
  function renderThumbnailGrid(category) {
    const grid = document.getElementById(`thumbnail-grid-${category.position}`);
    if (!grid) return;
    const buttons = grid.querySelectorAll('.bundle-thumbnail');
    const existing = getItemAt(category.position);
    buttons.forEach(btn => {
      const productIndex = parseInt(btn.dataset.productIndex, 10);
      const product = category.products[productIndex];
      const isActive = !!(existing && product && existing.product_id === product.id);
      btn.classList.toggle('active', isActive);
      btn.setAttribute('aria-selected', isActive ? 'true' : 'false');
    });
  }

  // Sizes hidden per-category via the block's "Hide these sizes" setting
  // (e.g. XS on Graphic Tee) — the variant still exists on the product,
  // it's just not offered here.
  function getVisibleVariants(category, product) {
    if (!product || !product.variants) return [];
    const hidden = (category.hiddenSizes || []).map(s => s.trim().toLowerCase()).filter(Boolean);
    if (!hidden.length) return product.variants;
    return product.variants.filter(v => !hidden.includes((v.title || '').trim().toLowerCase()));
  }

  function selectThumbnail(position, productIndex) {
    const category = getCategory(position);
    const product = getProduct(position, productIndex);
    if (!category || !product) return;

    // Clear any prior selection for this category — a new product means the
    // previous size no longer applies.
    setItemAt(position, null);

    const grid = document.getElementById(`thumbnail-grid-${position}`);
    if (grid) {
      grid.querySelectorAll('.bundle-thumbnail').forEach(btn => {
        const isActive = parseInt(btn.dataset.productIndex, 10) === productIndex;
        btn.classList.toggle('active', isActive);
        btn.setAttribute('aria-selected', isActive ? 'true' : 'false');
      });
    }

    renderSizeRow(category, product);
    updateComboImage();

    // Single-variant products (e.g. hats with no size options) have nothing
    // left to pick - the tile click is the whole selection.
    const visibleVariants = getVisibleVariants(category, product);
    if (visibleVariants.length === 1) {
      selectSize(category, product, visibleVariants[0], null);
    }
  }

  // ---------- Combination image (left side) ----------
  // Each combo_image block is labelled "row.col" (1.1, 1.2, 2.1 ... also
  // 1:1 or 1-1, extra text after is fine): row = category 1 pick, col =
  // category 2 pick, both 1-based. Images are looked up by label, so block
  // order doesn't matter. Unlabeled blocks fall back to their position
  // (row-major: 1.1, 1.2, ... 2.1). An unpicked category counts as its first
  // product, so 1.1 shows by default.
  let comboEntries = [];

  function parseComboLabel(label) {
    const match = /^\s*(\d+)\s*[.:\-]\s*(\d+)/.exec(label || '');
    return match ? `${parseInt(match[1], 10)}.${parseInt(match[2], 10)}` : null;
  }

  function buildComboMap(columns) {
    const map = {};
    comboEntries.forEach((entry, i) => {
      if (!entry || !entry.src) return;
      const key = parseComboLabel(entry.label) ||
        `${Math.floor(i / columns) + 1}.${(i % columns) + 1}`;
      if (!map[key]) map[key] = entry.src;
    });
    return map;
  }

  function pickedIndex(grid) {
    const active = grid.querySelector('.bundle-thumbnail.active');
    return active ? parseInt(active.dataset.productIndex, 10) : 0;
  }

  function updateComboImage() {
    if (!comboEntries.length) return;
    const grids = sectionEl.querySelectorAll('.bundle-thumbnail-grid');
    const mainImage = document.getElementById('gallery-main-image');
    if (grids.length < 2 || !mainImage) return;

    const columns = grids[1].querySelectorAll('.bundle-thumbnail').length || 1;
    const map = buildComboMap(columns);
    const key = `${pickedIndex(grids[0]) + 1}.${pickedIndex(grids[1]) + 1}`;
    const src = map[key] || map['1.1'];
    if (src && mainImage.getAttribute('src') !== src) mainImage.src = src;
  }

  // ---------- Shared Size Row ----------
  function renderSizeRow(category, product) {
    const row = document.getElementById(`size-row-${category.position}`);
    if (!row) return;
    row.innerHTML = '';

    const visibleVariants = getVisibleVariants(category, product);

    if (!product || visibleVariants.length === 0) {
      row.innerHTML = '<span class="bundle-size-placeholder">No sizes available for this product</span>';
      return;
    }

    // Single-variant products (e.g. hats) have no real size choice - the
    // variant title is just "Default Title", so there's nothing worth
    // showing here. selectThumbnail selects it directly.
    if (visibleVariants.length === 1) {
      return;
    }

    visibleVariants.forEach(variant => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'bundle-size-swatch';
      btn.setAttribute('role', 'radio');
      btn.textContent = variant.title;
      btn.dataset.variantId = variant.id;
      btn.setAttribute('aria-label', `${variant.title}${variant.available ? '' : ' (out of stock)'}`);

      if (!variant.available) {
        btn.disabled = true;
        btn.classList.add('disabled');
        btn.setAttribute('aria-disabled', 'true');
        btn.setAttribute('aria-checked', 'false');
      } else {
        btn.setAttribute('aria-checked', 'false');
        btn.addEventListener('click', () => selectSize(category, product, variant, btn));
      }

      row.appendChild(btn);
    });
  }

  function selectSize(category, product, variant, btn) {
    const row = document.getElementById(`size-row-${category.position}`);
    if (row) {
      row.querySelectorAll('.bundle-size-swatch').forEach(b => {
        b.classList.remove('selected');
        b.setAttribute('aria-checked', 'false');
      });
    }
    // btn is null for single-variant products auto-selected from the tile
    // click - there's no size swatch to mark.
    if (btn) {
      btn.classList.add('selected');
      btn.setAttribute('aria-checked', 'true');
    }

    setItemAt(category.position, {
      position: category.position,
      product_id: product.id,
      variant_id: variant.id,
      variant_title: variant.title,
      product_title: product.title,
      image_url: fixImageUrl(product.image),
      variant_price: variant.price
    });
  }

  // ---------- Restore selections on load (from storage) ----------
  function restoreSelections() {
    categories.forEach(category => {
      renderThumbnailGrid(category);
      const existing = getItemAt(category.position);
      if (existing) {
        const product = category.products.find(p => p.id === existing.product_id);
        if (product) {
          // If the saved pick belongs to a non-default group (e.g. the 7"
          // inseam), jump the tab there so its thumbnail is visible.
          const productIndex = category.products.indexOf(product);
          const thumb = document.querySelector(`.bundle-thumbnail[data-position="${category.position}"][data-product-index="${productIndex}"]`);
          if (thumb && thumb.dataset.group !== undefined) {
            setActiveGroup(category.position, parseInt(thumb.dataset.group, 10));
          }

          renderSizeRow(category, product);
          const row = document.getElementById(`size-row-${category.position}`);
          if (row) {
            const btn = row.querySelector(`.bundle-size-swatch[data-variant-id="${existing.variant_id}"]`);
            if (btn) {
              btn.classList.add('selected');
              btn.setAttribute('aria-checked', 'true');
            }
          }
          return;
        }
      }
      // No valid stored selection — show placeholder.
      const row = document.getElementById(`size-row-${category.position}`);
      if (row) row.innerHTML = '<span class="bundle-size-placeholder">Select a product above to choose a size</span>';
    });
    updateBundleTotal();
    updateCartMessage();
  }

  // ---------- Cart Message / Validation ----------
  function getIncompleteCategories() {
    return categories.filter(category => {
      const item = getItemAt(category.position);
      return !item;
    });
  }

  function updateCartMessage() {
    const messageEl = document.getElementById('bundle-cart-message');
    if (!messageEl) return;
    const incomplete = getIncompleteCategories();
    if (incomplete.length === 0 || !submitAttempted) {
      messageEl.hidden = true;
      messageEl.textContent = '';
    } else {
      messageEl.hidden = false;
      const names = incomplete.map(c => c.heading).join(', ');
      messageEl.textContent = `Please select a product and size for: ${names}`;
    }
  }

  // ---------- Add Bundle to Cart ----------
  async function addBundleToCart() {
    if (!parentVariantId || parentVariantId === '') {
      alert('Parent product not configured. Please select a bundle product in theme settings.');
      return;
    }
    submitAttempted = true;
    const incomplete = getIncompleteCategories();
    if (incomplete.length > 0) {
      updateCartMessage();
      return;
    }

    // One property carries both the customer-facing text (product + size)
    // and the variant id the aftersale webhook needs to deduct inventory —
    // plain numeric id, no "gid://.../" prefix, no quantity (always 1).
    // Hiding the id from customers happens in Liquid (snippets/bundle-pack-items.liquid),
    // not by omitting it here, so every line item still carries its id.
    const bundleItemsProperty = bundleItems
      .map(item => {
        // Single-variant products (e.g. hats) carry "Default Title" as
        // their only variant name - not worth showing to customers.
        const size = item.variant_title === 'Default Title' ? '' : ` (${item.variant_title})`;
        return `${item.product_title}${size} - ${item.variant_id}`;
      })
      .join('; ');

    const cartBtn = document.getElementById('add-all-to-cart');
    const originalText = cartBtn.textContent;
    cartBtn.textContent = 'Adding to Cart...';
    cartBtn.disabled = true;
    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: parseInt(parentVariantId),
          quantity: 1,
          properties: {
            'Items in Pack': bundleItemsProperty
          }
        })
      });
      if (!response.ok) throw new Error('Failed to add bundle');
      clearBundle();
      bundleItems = [];
      submitAttempted = false;
      if (redirectToCart) window.location.href = '/cart';
      else {
        document.dispatchEvent(new CustomEvent('cart:update'));
        cartBtn.textContent = 'Added!';
        setTimeout(() => { cartBtn.textContent = originalText; cartBtn.disabled = false; }, 2000);
      }
    } catch(e) {
      alert('Error adding bundle to cart.');
      cartBtn.textContent = originalText;
      cartBtn.disabled = false;
    }
  }

  // ---------- Gallery Thumbnails (Parent Product) ----------
  function initGallery() {
    const thumbnails = document.querySelectorAll('.gallery-thumbnail');
    const mainImage = document.getElementById('gallery-main-image');
    if (thumbnails.length && mainImage) {
      thumbnails.forEach(thumb => {
        thumb.addEventListener('click', () => {
          const fullImageUrl = thumb.dataset.fullImage;
          if (fullImageUrl) {
            mainImage.src = fullImageUrl;
            thumbnails.forEach(t => t.classList.remove('active'));
            thumb.classList.add('active');
          }
        });
      });
    }
  }

  // ---------- Responsive: update grid columns on resize ----------
  function handleResize() {
    const newIsMobile = window.innerWidth <= 768;
    const newCols = newIsMobile ? Math.min(gridColumns, 4) : gridColumns;
    document.documentElement.style.setProperty('--grid-columns', newCols);
  }

  // ---------- Initialization ----------
  function init() {
    const dataEl = document.getElementById('bundle-builder-data');
    if (dataEl) {
      try {
        categories = JSON.parse(dataEl.textContent);
      } catch(e) {
        console.error('Failed to parse bundle builder data', e);
        categories = [];
      }
    }

    const comboEl = document.getElementById('bundle-combo-images');
    if (comboEl) {
      try { comboEntries = JSON.parse(comboEl.textContent) || []; } catch(e) { comboEntries = []; }
    }

    loadBundle();
    initGallery();
    initGroupTabs();
    restoreSelections();
    updateComboImage();
    window.addEventListener('resize', handleResize);

    // Thumbnail click/keyboard handlers
    document.querySelectorAll('.bundle-thumbnail').forEach(btn => {
      btn.addEventListener('click', () => {
        const position = parseInt(btn.dataset.position, 10);
        const productIndex = parseInt(btn.dataset.productIndex, 10);
        selectThumbnail(position, productIndex);
      });
    });

    // Main "Add Bundle to Cart" button
    const cartBtn = document.getElementById('add-all-to-cart');
    if (cartBtn) cartBtn.addEventListener('click', addBundleToCart);

    // Optional: standalone parent product ATC
    const parentAtc = document.querySelector('.bundle-parent-atc');
    if (parentAtc) {
      parentAtc.addEventListener('click', async () => {
        const variantId = parentAtc.dataset.variantId;
        if (variantId) {
          try {
            await fetch('/cart/add.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ id: parseInt(variantId), quantity: 1 })
            });
            alert('Parent product added to cart!');
            document.dispatchEvent(new CustomEvent('cart:update'));
          } catch(e) { alert('Error adding parent product.'); }
        }
      });
    }
  }

  // Start everything when the DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
